/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {DiscountType, SubscriptionStatus, SubscriptionStatusMap} from '../util/interfaces';
import {ModelResponse, ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import User from "./userModel";
import {getToday} from "../util/utils";
import PaymentMethod from "./paymentMethodModel";
import {addSubscriptionModule, cancelSubscriptionModule, getSubscription} from "../util/requestSender";
import FailedPayment from "./failedPaymentModel";
import SubscriptionModule from "./subscriptionModuleModel";
import {SubscriptionModuleName, SubscriptionModuleStatus} from "../util/enums";

interface Props extends BaseProps {
    user_id?: string;
    status?: SubscriptionStatus;
    customer_id?: string;
    trial_end?: string;
    vat_number?: string;
    discount_type?: DiscountType | null;
    discount?: number;
    discount_until?: string;
    user?: User | any | null;
    payment_methods?: (PaymentMethod | any)[];
    subscription_id?: string;
    failed_payment?: FailedPayment | any | null;
    modules?: Array<SubscriptionModule | any>;
}

interface Fields extends BaseFields {
    user_id: string;
    status: SubscriptionStatus;
    customer_id: string;
    trial_end: string;
    vat_number: string;
    discount_type: DiscountType | null;
    discount: number;
    discount_until: string;
    user: User | null;
    payment_methods: PaymentMethod[];
    subscription_id: string;
    failed_payment: FailedPayment | null;
    modules: Array<SubscriptionModule>;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    user_id: '',
    status: SubscriptionStatus.INACTIVE,
    customer_id: '',
    trial_end: '',
    vat_number: '',
    discount: 0,
    discount_type: null,
    discount_until: '',
    user: null,
    payment_methods: new Array<PaymentMethod>(),
    subscription_id: '',
    failed_payment: null,
    modules: [],
};

const fieldsDefinition: { [key in keyof Omit<Fields, 'user' | 'payment_methods' | 'failed_payment'>]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    user_id: createFieldDef(ModelFieldType.STRING),
    status: createFieldDef(ModelFieldType.STRING, SubscriptionStatus.INACTIVE),
    customer_id: createFieldDef(ModelFieldType.STRING),
    trial_end: createFieldDef(ModelFieldType.STRING),
    vat_number: createFieldDef(ModelFieldType.STRING),
    discount_type: createFieldDef(ModelFieldType.STRING, null, true),
    discount: createFieldDef(ModelFieldType.NUMBER),
    discount_until: createFieldDef(ModelFieldType.STRING),
    subscription_id: createFieldDef(ModelFieldType.STRING),
    modules: createFieldDef(ModelFieldType.ARRAY),
};

const ignoreProps: Array<keyof Props> = ['user', 'payment_methods', 'failed_payment', 'modules'];

export default class Subscription extends BaseModel<Props, Fields> {
    static get = (accountID: string, callback: ModelResponse<Subscription | null>) => {
        getSubscription({
            account_id: accountID,
            callback: (data: ResponseData) => {
                const subscription: Subscription | null = data.success ? new Subscription(data.subscription) : null;
                callback(subscription, data.success, data);
            }
        })
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get userId(): string {
        return this.fields.user_id;
    }

    set userId(user_id: string) {
        this.fields.user_id = user_id;
    }

    get status(): SubscriptionStatus {
        const {status} = this.fields;
        if (status !== SubscriptionStatus.ACTIVE) return status;
        if (!this.hasPaymentMethod) return SubscriptionStatus.INACTIVE;
        return status;
    }

    set status(status: SubscriptionStatus) {
        this.fields.status = status;
    }

    get customerId(): string {
        return this.fields.customer_id
    }

    set customerId(customerId: string) {
        this.fields.customer_id = customerId;
    }

    get trialEnd(): string {
        return this.fields.trial_end;
    }

    get trialEndDate(): IDate | null {
        if (!this.trialEnd) return null;
        return new IDate(this.trialEnd);
    }

    get discountType(): DiscountType | null {
        return this.fields.discount_type;
    }

    get discount(): number {
        return this.fields.discount;
    }

    get discountUntil(): IDate | null {
        const {discount_until} = this.fields;
        if (!discount_until) return null;
        return new IDate(discount_until);
    }

    get user(): User | null {
        return this.fields.user;
    }

    set user(user: User | null) {
        this.fields.user = user;
        if (user) this.userId = user.id;
    }

    get isTrialing(): boolean {
        return this.status !== SubscriptionStatus.FREE &&
            (this.status === SubscriptionStatus.TRIAL || this.trialEndDate?.isAfter(getToday(false), 'd') === true)
    }

    get statusLabel(): string {
        const status = this.status;
        if (status === SubscriptionStatus.FREE || status === SubscriptionStatus.ACTIVE) return SubscriptionStatusMap[status];
        else if (this.isTrialing && this.status !== SubscriptionStatus.CANCELED)
            return SubscriptionStatusMap[SubscriptionStatus.TRIAL];
        return SubscriptionStatusMap[this.status];
    }

    get paymentMethods(): PaymentMethod[] {
        return this.fields.payment_methods;
    }

    get hasPaymentMethod(): boolean {
        return this.paymentMethods.length > 0;
    }

    get vatNumber(): string {
        return this.fields.vat_number;
    }

    set vatNumber(vatNumber: string) {
        this.fields.vat_number = vatNumber;
    }

    get subscriptionId(): string {
        return this.fields.subscription_id;
    }

    get failedPayment(): FailedPayment | null {
        return this.fields.failed_payment;
    }

    get modules(): Array<SubscriptionModule> {
        return this.fields.modules;
    }

    getModule = (moduleName: SubscriptionModuleName): SubscriptionModule | null => {
        return this.modules.find((module: SubscriptionModule) => module.name === moduleName) ?? null;
    }

    hasActiveModule = (moduleName: SubscriptionModuleName,
                       excludeStatuses: Array<SubscriptionModuleStatus> = []): boolean => {
        if (this.status === SubscriptionStatus.CANCELED) return false;
        return !!this.modules.find(
            (module: SubscriptionModule) => {
                if (module.name !== moduleName) return false;
                return module.isActive(excludeStatuses);
            });
    };

    addModule = (params: {
        accountID: string; moduleName: SubscriptionModuleName; checkExists?: boolean;
    }, callback: ModelResponse<Subscription>) => {
        return addSubscriptionModule({
            data: {name: params.moduleName, account_id: params.accountID, check_exists: params.checkExists},
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.subscription);
                callback(this, data.success, data);
            }
        });
    };

    cancelModule = (params: {accountID: string, moduleName: SubscriptionModuleName},
                    callback: ModelResponse<Subscription>) => {
        return cancelSubscriptionModule({
            account_id: params.accountID, name: params.moduleName, callback: (data: ResponseData) => {
                if (data.success) this.build(data.subscription);
                callback(this, data.success, data);
            }
        });
    };

    save = (callback?: ModelResponse<Subscription>) => {

    };

    update = (callback?: ModelResponse<Subscription>) => {

    };

    delete = (callback?: ModelResponse<Subscription>) => {

    };

    hasActiveDiscount = (): boolean => {
        if (!this.discount || !this.discountType) return false;
        return !this.discountUntil?.isBefore(IDate.now());
    }

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.user) {
                this.user = props.user instanceof User ? props.user : new User(props.user);
            }
            if (props.payment_methods) {
                const methods: PaymentMethod[] = [];
                for (const meth of props.payment_methods) {
                    const method: PaymentMethod = meth instanceof PaymentMethod ? meth : new PaymentMethod(meth);
                    method.subscription = this;
                    methods.push(method);
                }
                this.fields.payment_methods = methods;
            }
            if (props.failed_payment)
                this.fields.failed_payment = props.failed_payment instanceof FailedPayment ? props.failed_payment :
                    new FailedPayment(props.failed_payment);
            if (props.modules) {
                const modules: Array<SubscriptionModule> = [];
                for (const mod of props.modules) {
                    const module = mod instanceof SubscriptionModule ? mod : new SubscriptionModule(mod);
                    modules.push(module);
                }
                this.fields.modules = modules;
            }
        }
    }
}